import React, { useState } from 'react';
import * as Icons from 'lucide-react';
import { cn } from '@/lib/utils';
import { catalog_path } from '@/lib/routes';
import type { LucideIcon } from 'lucide-react';
import type { WorkflowDetail, NavigationSection } from './types';
import { navigationItems, colorMap } from './types';
import { DescriptionSection } from './sections/DescriptionSection';
import { InputsSection } from './sections/InputsSection';
import { ActivitiesSection } from './sections/ActivitiesSection';
import { RunSection } from './sections/RunSection';
import { ExecutionsSection } from './sections/ExecutionsSection';

interface WorkflowShowProps {
  workflow: WorkflowDetail;
}

const Show = ({ workflow }: WorkflowShowProps) => {
  const [activeSection, setActiveSection] = useState<NavigationSection>(() => {
    // Get tab from URL query parameter
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab') as NavigationSection;
    return tab && navigationItems.some((item) => item.id === tab) ? tab : 'description';
  });

  const Icon = Icons[workflow.icon] as LucideIcon;
  const colors = colorMap[workflow.colorScheme as keyof typeof colorMap] || colorMap.zinc;

  const handleSectionChange = (section: NavigationSection) => {
    setActiveSection(section);
    // Update URL with the new tab
    const url = new URL(window.location.href);
    url.searchParams.set('tab', section);
    window.history.pushState({}, '', url);
  };

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'description':
        return <DescriptionSection workflow={workflow} />;
      case 'inputs':
        return <InputsSection workflow={workflow} />;
      case 'activities':
        return <ActivitiesSection workflow={workflow} />;
      case 'run':
        return <RunSection workflow={workflow} />;
      case 'executions':
        return <ExecutionsSection workflow={workflow} />;
      default:
        return <DescriptionSection workflow={workflow} />;
    }
  };

  return (
    <div className="flex flex-col h-screen">
      {/* Header */}
      <header className="sticky top-0 z-10 flex h-14 md:h-16 shrink-0 items-center gap-2 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="flex items-center gap-2 px-3 md:px-4">
          <nav className="flex items-center gap-2 text-sm" aria-label="Breadcrumb">
            <a
              href={catalog_path(workflow.provider)}
              className="text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-md px-1 py-1"
            >
              <Icons.ChevronLeft className="w-4 h-4" />
              <span>Workflow Catalog</span>
            </a>
          </nav>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <div className="w-64 border-r flex-shrink-0">
          <div className="p-4 border-b">
            <div className="flex items-center gap-2">
              <div className={cn('p-2 rounded-md', colors.bg)}>
                <Icon className={cn('w-4 h-4', colors.text)} />
              </div>
              <span className="font-medium text-sm">{workflow.title}</span>
            </div>
          </div>
          <div className="p-2">
            <div className="space-y-1">
              {navigationItems.map((item) => {
                const ItemIcon = Icons[item.icon] as LucideIcon;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleSectionChange(item.id)}
                    className={cn(
                      'w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-md transition-colors',
                      activeSection === item.id
                        ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    )}
                  >
                    <ItemIcon className="w-4 h-4" />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Workflow Title Section */}
          <div className="w-full px-4 md:px-6 py-4 md:py-6 border-b flex-shrink-0">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex items-center gap-3 md:gap-4 min-w-0 flex-1">
                <div className={cn('p-2.5 md:p-3 rounded-lg flex-shrink-0', colors.bg)}>
                  <Icon className={cn('w-6 h-6 md:w-8 md:h-8', colors.text)} />
                </div>
                <div className="min-w-0 flex-1">
                  <h1 className="text-2xl md:text-3xl font-bold tracking-tight truncate">
                    {workflow.title}
                  </h1>
                  <div className="text-xs md:text-sm text-muted-foreground mt-1 truncate">
                    {workflow.path
                      ?.split('/')
                      ?.slice(0, -1)
                      .map((segment, index, array) => (
                        <React.Fragment key={index}>
                          <span>{segment}</span>
                          {index < array.length - 1 && (
                            <span className="text-muted-foreground/50 mx-1">/</span>
                          )}
                        </React.Fragment>
                      ))}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
                <button
                  onClick={() => handleSectionChange('run')}
                  className="flex items-center gap-2 px-4 md:px-6 py-2 md:py-2.5 text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 rounded-lg transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                >
                  <Icons.Play className="w-4 h-4" />
                  <span className="hidden sm:inline">Run Workflow</span>
                  <span className="sm:hidden">Run</span>
                </button>
                <a
                  href={`https://github.com/growthxai/flow/tree/main/packages/temporal/src/workflows/${workflow.path}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 md:px-4 py-2 md:py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground border border-input rounded-lg hover:border-border transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                >
                  <Icons.Github className="w-4 h-4" />
                  <span className="hidden sm:inline">View Source</span>
                  <span className="sm:hidden">Source</span>
                </a>
              </div>
            </div>
          </div>

          {/* Content */}
          <main className="px-4 md:px-6 py-6 md:py-8">
            <div className="max-w-4xl space-y-6 md:space-y-8">{renderActiveSection()}</div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default Show;
